import { Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { Observable, of, map } from 'rxjs';
import moment from 'moment';

@Injectable({
  providedIn: 'root'
})
export class PropertyService {
  constructor(
    private http: HttpClient,
    private jwtHelper: JwtHelperService
  ) { }

  getDecodedToken(): any {
    const token = localStorage.getItem('accessToken');
    if (token && !this.jwtHelper.isTokenExpired(token)) {
      const decodedToken = this.jwtHelper.decodeToken(token);
      return decodedToken;
    }
    return null;
  }

  getUserInfo(): Observable<any> {
    const decodedToken = this.getDecodedToken();
    if (!decodedToken) return of(null);
    console.log('decodedToken.property_id', decodedToken.property_id)
    if (decodedToken.property_id) {
      return this.getPropertyById(decodedToken.property_id).pipe(
        map((property) => ({
          ...decodedToken,
          fullName: decodedToken.fullname,
          property: { name: property.name, timezone: property.timezone, modelUnit: property.modelUnit },
        }))
      );
    } else {
      console.log('No property_id in token');
      console.log('decodedToken', decodedToken);
      return of(decodedToken);
    }
  }

  getPropertyByLandlordId(landlorId: string): Observable<any> {
    return this.http.get<any>(`${environment.baseUrl}properties/landlord/${landlorId}`);
  }

  getPropertyById(propertyId: string): Observable<any> {
    return this.http.get<any>(`${environment.baseUrl}properties/property/${propertyId}`);
  }

  getPropertyInfo(): Observable<any> {
    const decodedToken = this.getDecodedToken();
    console.log("Property Info property_id =>",decodedToken)
    if (!decodedToken) return of(null);
    return this.http.get<any>(`${environment.baseUrl}properties/property-info/${decodedToken.property_id}`);
  }

  updatePropertyInfo(propertyData: any): Observable<any> {
    const decodedToken = this.getDecodedToken();
    if (!decodedToken) return of(null);
    return this.http.put<any>(
      `${environment.baseUrl}properties/property-info/${decodedToken.property_id}`,
      propertyData
    );
  }

  getCompanyById(companyId: number) {
    return this.http.get<any>(`${environment.baseUrl}/auth/get-company-by-id/${companyId}`);
  }

  getPrompts() {
    return this.http.get<any>(`${environment.baseUrl}properties/prompts`);
  }

  updatePrompt(slug: string, data: any) {
    return this.http.put(`${environment.baseUrl}properties/prompt/${slug}`, data);
  }

  updateCompanyLimits(companyId: any, dialogueLimit: any, audioLimit: any) {
    return this.http.put<any>(`${environment.baseUrl}/auth/update-company-limits`, { companyId, dialogueLimit, audioLimit });
  }

  saveAmenityCategory(amenityCategoryData: any) {
    return this.http.post<any>(`${environment.baseUrl}amenity-categories/create-category`, amenityCategoryData);
  }

  saveAmenity(amenityData: any) {
    return this.http.post<any>(`${environment.baseUrl}amenities/create-amenity`, amenityData);
  }

  updateAmenity(id: string, data: any) {
    return this.http.put(`${environment.baseUrl}amenities/${id}`, data);
  }

  saveCategory(categoryData: any) {
    return this.http.post<any>(`${environment.baseUrl}/property-categories`, categoryData);
  }

  getAmenityCategories() {
    return this.http.get<any>(`${environment.baseUrl}amenity-categories/all-amenities`);
  }

  getAllAmenities(params?: any) {
    let httpParams = new HttpParams();
    if (params) {
      if (params.page) httpParams = httpParams.set('page', params.page.toString());
      if (params.limit) httpParams = httpParams.set('limit', params.limit.toString());

      if (params.sortField) {
        httpParams = httpParams.set('sortField', params.sortField);
        httpParams = httpParams.set('sortOrder', params.sortOrder?.toString() || '1');
      }

      if (params.category) httpParams = httpParams.set('category', params.category);
      if (params.search) httpParams = httpParams.set('search', params.search);

      if (params.propertyId) httpParams = httpParams.set('propertyId', params.propertyId);
    }
    return this.http.get(
      `${environment.baseUrl}amenities/get-all-amenities`,
      { params: httpParams }
    );
  }

  getAmenitiesByName(name: string, propertyId: string) {
    return this.http.get<any>(`${environment.baseUrl}amenities/amenitiesbyname/${name}?propertyId=${propertyId}`);
  }

  deleteAmenity(id: string) {
    return this.http.delete(`${environment.baseUrl}amenities/${id}`);
  }

  getAllPropertyCategories() {
    return this.http.get<any>(`${environment.baseUrl}property-categories/get-all-property-categories`);
  }

  getAllFloorPlans(propertyId: string, queryParams: any) {
    return this.http.get<any>(`${environment.baseUrl}floor-plan/${propertyId}`, { params: queryParams });
  }

  getFloorPlans() {
    return this.http.get<any>(`${environment.baseUrl}floor-plan`);
  }

  getAllUnits(propertyId: string, queryParams: any) {
    return this.http.get<any>(`${environment.baseUrl}units/${propertyId}`, { params: queryParams });
  }

  getUnitById(unitId: string | number) {
    return this.http.get<any>(`${environment.baseUrl}units/detail/${unitId}`);
  }
  

  getVisibleUnits(propertyId: string, queryParams: any) {
    return this.http.get<any>(`${environment.baseUrl}units/visible/${propertyId}`, { params: queryParams });
  }

  createFloorPlan(floorPlanData: any) {
    return this.http.post<any>(`${environment.baseUrl}floor-plan/create`, floorPlanData);
  }

  createUnit(unit: any) {
    return this.http.post<any>(`${environment.baseUrl}units/create`, unit);
  }

  updateFloorPlan(floorPlanId: number, floorPlanData: any) {
    return this.http.put<any>(`${environment.baseUrl}floor-plan/${floorPlanId}`, floorPlanData);
  }

  updateUnit(unitId: number, unitData: any) {
    return this.http.put<any>(`${environment.baseUrl}units/${unitId}`, unitData);
  }

  deleteImage(imageId: string) {
    return this.http.delete<any>(`${environment.baseUrl}images/${imageId}`);
  }


  deleteFloorPlan(floorPlanId: number) {
    return this.http.delete<any>(`${environment.baseUrl}floor-plan/${floorPlanId}`);
  }

  deleteUnit(unitId: number) {
    return this.http.delete<any>(`${environment.baseUrl}units/${unitId}`);
  }

  savePropertyCategory(categoryData: any) {
    return this.http.post<any>(`${environment.baseUrl}property-categories/create-property-category`, categoryData);
  }

  createUser(userData: any) {
    return this.http.post<any>(`${environment.baseUrl}users/create-user`, userData);
  }

  createTenant(tenantData: any) {
    return this.http.post<any>(`${environment.baseUrl}users/create-tenant`, tenantData);
  }

  getTenants(queryParams: any): Observable<any> {
    let params = new HttpParams();
    if (queryParams.page) {
      params = params.set('page', queryParams.page);
    }
    if (queryParams.limit) {
      params = params.set('limit', queryParams.limit);
    }
    if (queryParams.sortField) {
      if (queryParams.sortField == 'name') {
        queryParams.sortField = 'first_name';
      }
      params = params.set('sortField', queryParams.sortField);
    }
    if (queryParams.sortOrder) {
      params = params.set('sortOrder', queryParams.sortOrder);
    }
    if (queryParams.filters) {
      params = params.set('filters', queryParams.filters);
    }
    return this.http.get<any>(`${environment.baseUrl}users/get-tenant`, { params });
  }

  getAdminUsers(params: any): Observable<{ data: any[]; total: number }> {
    if (!params.sortField) {
      params.sortField = 'first_name';
    }
    if (!params.sortOrder) {
      params.sortOrder = -1;
    }
    return this.http.get<{ data: any[]; total: number }>(`${environment.baseUrl}users/get-users`, { params });
  }

  getAllUsers(userType?: string) {
    const url = `${environment.baseUrl}users/all-users`;
    const options = userType ? { params: { user_type: userType } } : {};
    return this.http.get<any[]>(url, options);
  }

  getAllFollowups(queryParams: any): Observable<any> {
    return this.http.get<any>(`${environment.baseUrl}users/get-all-followups`, { params: queryParams });
  }

  getCrmFollowups(queryParams: any): Observable<any> {
    return this.http.get<any>(`${environment.baseUrl}users/get-crm-followups`, { params: queryParams });
  }

  getCrmSummary(): Observable<any> {
    return this.http.get<any>(`${environment.baseUrl}users/crm-summary`);
  }

  createFollowup(followup: any): Observable<any> {
    return this.http.post<any>(`${environment.baseUrl}users/create-followup`, followup);
  }

  deleteFollowup(followupId: string): Observable<any> {
    return this.http.delete<any>(`${environment.baseUrl}users/delete-followup/${followupId}`);
  }


  getWeekAvailabilityReport(propertyId: string, startDate: string, endDate: string): Observable<any[]> {
    const decoded = this.getDecodedToken();
    const landlordId = decoded?.user_id?.toString() ?? '';
    return this.http.get<any[]>(`${environment.baseUrl}reports/week-availability`, {
      params: { propertyId, landlordId, startDate, endDate }
    });
  }

  getFollowUpsSummary(startDate: Date, endDate: Date) {
    const formattedStartDate = moment(startDate).format('YYYY-MM-DD');
    const formattedEndDate = moment(endDate).format('YYYY-MM-DD');

    const params = { start_date: formattedStartDate, end_date: formattedEndDate };
    return this.http.get<any>(`${environment.baseUrl}users/get-followups-summary`, { params });
  }

  getAllCountries() {
    return this.http.get<any>(`${environment.baseUrl}location/countries`);
  }

  getStatesByCountry(countryid: number) {
    return this.http.get<any>(`${environment.baseUrl}location/country/states/${countryid}`);
  }

  getCitiesByStates(stateName: string) {
    return this.http.get<any>(`${environment.baseUrl}location/country/cities/${stateName}`);
  }


  createProperty(propertyData: any) {
    return this.http.post(`${environment.baseUrl}properties/create`, propertyData);
  }

  updateProperty(id: string, propertyData: any) {
    return this.http.put(`${environment.baseUrl}properties/edit/${id}`, propertyData);
  }

  updatePropertyBySuperAdmin(id: string, propertyData: any) {
    console.log('Updating property with ID:', id);
    console.log('Property data being sent:', propertyData);
    return this.http.put(`${environment.baseUrl}properties/edit-by-super-admin/${id}`, propertyData);
  }

  getAllProperties() {
    return this.http.get<any>(`${environment.baseUrl}properties/all`);
  }

  // Yardi Properties List
  getAllYardiProperties() {
    return this.http.get<any>(`${environment.baseUrl}property-yardi/list`);
  }

  getEventsList() {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${environment.calendlyToken}`
    });
    const params = new HttpParams().set('organization', 'https://api.calendly.com/organizations/f8798c49-9a58-4f5c-9351-9e5be0fe898b');
    return this.http.get<any>(environment.calendlyUrl, { headers, params });
  }

  getEventDetails(eventId: string) {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${environment.calendlyToken}`
    });
    const params = new HttpParams().set('uuid', eventId);
    // /${eventId}
    return this.http.get<any>(`${environment.calendlyEventDetail}/${eventId}/invitees`, { headers });
  }

  deteleUserOrTenant(id: string) {
    return this.http.delete(`${environment.baseUrl}users/${id}`)
  }

  updateTenant(id: string, data: string) {
    return this.http.put<any>(`${environment.baseUrl}users/update-tenant/${id}`, data);
  }

  updateTenantStatus(id: string, isActive: boolean) {
    return this.http.patch<any>(`${environment.baseUrl}users/tenant/${id}/status`, { isActive });
  }

  updateUser(id: string, data: string) {
    return this.http.put<any>(`${environment.baseUrl}users/update-user/${id}`, data);
  }

  sendTenantCredentials(userId: string) {
    return this.http.post<any>(`${environment.baseUrl}users/${userId}/send-credentials`, {});
  }

  changePassword(newPassword: string) {
    return this.http.post<any>(`${environment.baseUrl}users/change-password`, { newPassword });
  }

  getNotificationPreferences() {
    return this.http.get<any>(`${environment.baseUrl}users/email-notifications/get`);
  }

  updateNotificationPreferences(prefs: any) {
    return this.http.patch<any>(`${environment.baseUrl}users/email-notifications/save`, prefs);
  }

  saveAvailability(weeklyHours: any) {
    return this.http.post<any>(`${environment.baseUrl}users/save-availability`, weeklyHours);
  }

  getNotifications(): Observable<any[]> {
    return this.http.get<any>(`${environment.baseUrl}users/get/notifications`);
  }

  resetPassword(currentPassword: string, newPassword: string): Observable<any> {
    return this.http.post<any>(`${environment.baseUrl}users/reset-password`, { currentPassword, newPassword });
  }

  getPropertyDetails() {
    return this.http.get<any>(`${environment.baseUrl}properties`);
  }

  createTour(tourData: any) {
    return this.http.post<any>(`${environment.baseUrl}tours/create-tour`, tourData);
  }

  updateTour(id: string, data: string) {
    return this.http.put<any>(`${environment.baseUrl}tours/update-tour/${id}`, data);
  }

  addAmenityToProperty(propertyId: string, amenityId: string) {
    return this.http.post(`${environment.baseUrl}properties/${propertyId}/amenities`, { amenityId });
  }

  /**
  * Bulk update units for availability or visibility
  * @param payload Object containing unit_ids and fields to update
  */
  bulkUpdate(payload: any): Observable<any> {
    return this.http.post(`${environment.baseUrl}units/bulk-update`, payload);
  }

  impersonateToLandlordPortal(propertyId: string): Observable<any> {
    if (propertyId) {
      return this.http.post<any>(`${environment.baseUrl}properties/impersonate/${propertyId}`, {});
    }
    return of(null);
  }

  getAllUnitFilters(propertyId: string) {
    return this.http.get<any>(`${environment.baseUrl}units/${propertyId}/filters`);
  }

  getFloorPlanFilters(propertyId: string) {
    return this.http.get<any>(`${environment.baseUrl}floor-plan/${propertyId}/floorplan-filters`);
  }

  getFloorPlanFiltersOnly(propertyId: string) {
    return this.http.get<any>(`${environment.baseUrl}floor-plan/${propertyId}/floorplan-filters-only`);
  }
}
